import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Truck, MagnifyingGlass, Barcode, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { AppHeader } from "@/components/AppHeader";
import { DateField } from "@/components/DateTimeField";
import { fmtMoney } from "@/lib/poFormat";
import { fmtSqft, lineSqft, lineTotal, computedLineTotal, hasOverride } from "@/lib/poPricing";
import { BarcodeScanDialog } from "@/components/BarcodeScanDialog";
import { CURRENCIES, PAYMENT_TERMS } from "@/lib/poFormat";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY_LINE = {
  kind: "slab",
  slab_id: "",
  slab_color: "",
  material: "Granite",
  finish: "Polished",
  thickness: "3cm",
  length_in: "",
  width_in: "",
  qty: 1,
  unit_cost: "",
};

export default function PurchaseOrderForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newSup, setNewSup] = useState(null);
  const [form, setForm] = useState({
    supplier_id: "none",
    customer_id: "none",
    job_id: "none",
    order_date: "",
    expected_date: "",
    status: "Draft",
    reference: "",
    currency: "USD",
    payment_terms: "",
    additional_charges: [],
    notes: "",
    consignment: false,
    lines: [{ ...EMPTY_LINE }],
  });
  const [catalog, setCatalog] = useState({ slabs: [], sinks: [] });
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [lowStock, setLowStock] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    api
      .get("/customers")
      .then(({ data }) => setCustomers(data))
      .catch(() => setCustomers([]));
    api
      .get("/jobs")
      .then(({ data }) => setJobs(data))
      .catch(() => setJobs([]));
    api
      .get("/slabs/low-stock")
      .then(({ data }) => setLowStock(data.items || []))
      .catch(() => setLowStock([]));
  }, []);

  useEffect(() => {
    api.get("/inventory/catalog").then(({ data }) => {
      setCatalog(data);
      if (id) return;
      const color = searchParams.get("slab_color");
      const sinkKey = searchParams.get("sink_key");
      const qty = Number(searchParams.get("qty")) || 2;
      const projectId = searchParams.get("project");
      if (projectId || searchParams.get("customer") || searchParams.get("job")) {
        setForm((f) => ({
          ...f,
          project_id: projectId || "",
          customer_id: searchParams.get("customer") || f.customer_id,
          job_id: searchParams.get("job") || f.job_id,
        }));
      }
      if (color) {
        const src = data.slabs.find(
          (s) => (s.slab_color || "").toLowerCase() === color.toLowerCase(),
        );
        setForm((f) => ({
          ...f,
          status: "Ordered",
          lines: [
            src
              ? {
                  ...EMPTY_LINE,
                  slab_color: src.slab_color,
                  material: src.material,
                  finish: src.finish,
                  thickness: src.thickness,
                  length_in: src.length_in,
                  width_in: src.width_in,
                  unit_cost: src.unit_cost ?? "",
                  qty,
                }
              : { ...EMPTY_LINE, slab_color: color, qty },
          ],
        }));
        toast.success(`${color} added — reordering low stock`);
      } else if (sinkKey) {
        const src = data.sinks.find((s) => s.key === sinkKey);
        if (src) {
          setForm((f) => ({
            ...f,
            status: "Ordered",
            lines: [
              {
                ...EMPTY_LINE,
                kind: "sink",
                sink_id: src.key,
                brand: src.brand,
                model: src.model,
                description: src.description,
                sink_type: src.sink_type,
                size: src.size,
                unit_cost: src.unit_cost ?? "",
                qty,
              },
            ],
          }));
          toast.success(
            `${[src.brand, src.model].filter(Boolean).join(" ")} added — reordering low stock`,
          );
        }
      }
    });
  }, [id, searchParams]);

  const pullFromInventory = (value) => {
    if (value === "none") return;
    const kind = value.slice(0, value.indexOf(":"));
    const key = value.slice(value.indexOf(":") + 1);
    const src =
      kind === "slab"
        ? catalog.slabs.find((s) => s.key === key)
        : catalog.sinks.find((s) => s.key === key);
    if (!src) return;
    const line =
      kind === "slab"
        ? {
            ...EMPTY_LINE,
            kind: "slab",
            slab_id: src.slab_id || "",
            slab_color: src.slab_color,
            material: src.material,
            finish: src.finish,
            thickness: src.thickness,
            length_in: src.length_in,
            width_in: src.width_in,
            unit_cost: src.unit_cost ?? "",
            qty: 1,
          }
        : {
            ...EMPTY_LINE,
            kind: "sink",
            sink_id: src.key,
            brand: src.brand,
            model: src.model,
            description: src.description,
            sink_type: src.sink_type,
            size: src.size,
            unit_cost: src.unit_cost ?? "",
            qty: 1,
          };
    setForm((f) => ({ ...f, lines: [...f.lines, line] }));
    maybePrefillSupplier(src.supplier);
    toast.success(
      `${
        kind === "slab"
          ? src.slab_color
          : [src.brand, src.model].filter(Boolean).join(" ")
      } added to this order`,
    );
  };

  const maybePrefillSupplier = (supplierName) => {
    if (!supplierName) return;
    setForm((f) => {
      if (f.supplier_id !== "none") return f;
      const sup = suppliers.find(
        (s) => (s.name || "").toLowerCase() === supplierName.toLowerCase(),
      );
      if (!sup) return f;
      return {
        ...f,
        supplier_id: sup.id,
        currency: sup.currency || f.currency,
        payment_terms: sup.payment_terms || f.payment_terms,
      };
    });
  };

  const addByBarcode = async (code) => {
    try {
      const { data } = await api.get("/inventory/by-barcode", {
        params: { code },
      });
      if (!data.found) {
        toast.error(`No item found for “${code}”`);
        return;
      }
      const l = data.line;
      setForm((f) => ({
        ...f,
        lines: [...f.lines, { ...EMPTY_LINE, ...l, qty: 1 }],
      }));
      maybePrefillSupplier(l.supplier);
      toast.success(
        `${l.slab_color || [l.brand, l.model].filter(Boolean).join(" ") || "Item"} added${
          data.code ? ` (${data.code})` : ""
        }`,
      );
      setScanOpen(false);
    } catch (err) {
      toast.error("Barcode lookup failed");
    }
  };

  const addLowStock = (row) => {
    const src = catalog.slabs.find(
      (s) =>
        (s.slab_color || "").toLowerCase() ===
        (row.slab_color || "").toLowerCase(),
    );
    const line = src
      ? {
          ...EMPTY_LINE,
          kind: "slab",
          slab_id: src.slab_id || "",
          slab_color: src.slab_color,
          material: src.material,
          finish: src.finish,
          thickness: src.thickness,
          length_in: src.length_in,
          width_in: src.width_in,
          unit_cost: src.unit_cost ?? "",
          qty: 2,
        }
      : { ...EMPTY_LINE, kind: "slab", slab_color: row.slab_color, qty: 2 };
    setForm((f) => ({ ...f, lines: [...f.lines, line] }));
    if (src) maybePrefillSupplier(src.supplier);
    setPickerOpen(false);
    toast.success(`${row.slab_color} added to reorder`);
  };

  const load = useCallback(async () => {
    const sups = await api.get("/suppliers");
    setSuppliers(sups.data);
    if (id) {
      const { data } = await api.get(`/purchase-orders/${id}`);
      setForm({
        supplier_id: data.supplier_id || "none",
        customer_id: data.customer_id || "none",
        job_id: data.job_id || "none",
        order_date: data.order_date || "",
        expected_date: data.expected_date || "",
        status: data.status,
        reference: data.reference || "",
        currency: data.currency || "USD",
        payment_terms: data.payment_terms || "",
        additional_charges: (data.additional_charges || []).map((c) => ({
          id: c.id,
          label: c.label,
          amount: c.amount,
        })),
        notes: data.notes || "",
        consignment: !!data.consignment,
        lines: data.lines.length ? data.lines : [{ ...EMPTY_LINE }],
      });
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const setLine = (i, k, v) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)),
    }));

  const addLine = (kind) =>
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        kind === "sink"
          ? {
              ...EMPTY_LINE,
              kind: "sink",
              brand: "",
              model: "",
              sink_type: "Undermount",
              size: "",
            }
          : { ...EMPTY_LINE, kind: "slab" },
      ],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const supplier = suppliers.find((s) => s.id === form.supplier_id);
    const payload = {
      supplier_id: form.supplier_id === "none" ? null : form.supplier_id,
      supplier_name: supplier?.name || "",
      customer_id: form.customer_id === "none" ? null : form.customer_id,
      job_id: form.job_id === "none" ? null : form.job_id,
      project_id: form.project_id || null,
      job_code: jobs.find((j) => j.id === form.job_id)?.tag || "",
      customer_name:
        customers.find((c) => c.id === form.customer_id)?.name || "",
      customer_company:
        customers.find((c) => c.id === form.customer_id)?.company || "",
      order_date: form.order_date,
      expected_date: form.expected_date,
      status: form.status,
      reference: form.reference,
      currency: form.currency,
      payment_terms: form.payment_terms,
      additional_charges: (form.additional_charges || [])
        .filter((c) => (c.label || "").trim() || Number(c.amount))
        .map((c) => ({
          id: c.id,
          label: c.label,
          amount: Number(c.amount) || 0,
        })),
      notes: form.notes,
      consignment: !!form.consignment,
      lines: form.lines
        .filter((l) =>
          l.kind === "sink" ? (l.model || "").trim() : l.slab_color.trim(),
        )
        .map((l) => ({
          ...l,
          kind: l.kind || "slab",
          length_in: Number(l.length_in) || 0,
          width_in: Number(l.width_in) || 0,
          qty: Number(l.qty) || 1,
          unit_cost: l.unit_cost === "" ? null : Number(l.unit_cost),
          total_override:
            l.total_override === "" || l.total_override == null ? null : Number(l.total_override),
        })),
    };
    try {
      const { data } = id
        ? await api.put(`/purchase-orders/${id}`, payload)
        : await api.post("/purchase-orders", payload);
      toast.success(id ? "Purchase order updated" : `${data.code} created`);
      navigate(`/inventory/purchase-orders/${data.id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Edit PO" : "New PO"} />
      <main className="relative z-10 pb-28 pt-6">
        <button
          onClick={() => navigate("/inventory/purchase-orders")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> All purchase orders
        </button>
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-zinc-600">
            Inventory
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {id ? "Edit Purchase Order" : "New Purchase Order"}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Pick a supplier, add slab or sink lines, then receive them into
            stock when they land
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5"
          data-testid="po-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Supplier
              </Label>
              {newSup !== null ? (
                <div className="flex gap-2" data-testid="new-supplier-row">
                  <Input
                    data-testid="new-supplier-name"
                    autoFocus
                    value={newSup}
                    onChange={(e) => setNewSup(e.target.value)}
                    placeholder="Supplier name"
                    className="h-12 border-zinc-800 bg-zinc-900"
                  />
                  <Button
                    type="button"
                    data-testid="save-new-supplier"
                    onClick={async () => {
                      if (!newSup.trim()) return;
                      const { data } = await api.post("/suppliers", {
                        name: newSup.trim(),
                      });
                      setSuppliers((list) => [...list, data]);
                      setForm((f) => ({ ...f, supplier_id: data.id }));
                      setNewSup(null);
                      toast.success(`${data.name} added`);
                    }}
                    className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    data-testid="cancel-new-supplier"
                    variant="outline"
                    onClick={() => setNewSup(null)}
                    className="h-12 border-zinc-700 bg-transparent px-3"
                  >
                    <X size={14} weight="bold" />
                  </Button>
                </div>
              ) : (
                <Select
                  value={form.supplier_id}
                  onValueChange={(v) => {
                    const sup = suppliers.find((s) => s.id === v);
                    setForm((f) => ({
                      ...f,
                      supplier_id: v,
                      currency: sup?.currency || f.currency,
                      payment_terms: sup?.payment_terms || f.payment_terms,
                    }));
                  }}
                >
                  <SelectTrigger
                    data-testid="po-supplier-select"
                    className="h-12 border-zinc-800 bg-zinc-900"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    <SelectItem value="none">No supplier</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {newSup === null && (
                <button
                  type="button"
                  data-testid="add-supplier-inline"
                  onClick={() => setNewSup("")}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                >
                  <Truck size={12} weight="bold" /> Add supplier
                </button>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">
                For account (optional)
              </Label>
              <Select
                value={form.customer_id}
                onValueChange={(v) => setForm({ ...form, customer_id: v })}
              >
                <SelectTrigger
                  data-testid="po-customer-select"
                  className="h-12 border-zinc-800 bg-zinc-900"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <SelectItem value="none">Stock order (no account)</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">
                For job (optional)
              </Label>
              <Select
                value={form.job_id}
                onValueChange={(v) => {
                  const job = jobs.find((j) => j.id === v);
                  setForm({
                    ...form,
                    job_id: v,
                    customer_id:
                      job?.customer_id && form.customer_id === "none"
                        ? job.customer_id
                        : form.customer_id,
                  });
                }}
              >
                <SelectTrigger
                  data-testid="po-job-select"
                  className="h-12 border-zinc-800 bg-zinc-900"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <SelectItem value="none">Not job specific</SelectItem>
                  {jobs
                    .filter(
                      (j) =>
                        form.customer_id === "none" ||
                        j.customer_id === form.customer_id,
                    )
                    .map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.tag} — {j.project_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Status
              </Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger
                  data-testid="po-status-select"
                  className="h-12 border-zinc-800 bg-zinc-900"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {["Draft", "Ordered", "Received", "Cancelled"].map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Currency
              </Label>
              <Select
                value={form.currency}
                onValueChange={(v) => setForm({ ...form, currency: v })}
              >
                <SelectTrigger
                  data-testid="po-currency-select"
                  className="h-12 border-zinc-800 bg-zinc-900"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {CURRENCIES.map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Payment terms
              </Label>
              <Select
                value={form.payment_terms || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, payment_terms: v === "none" ? "" : v })
                }
              >
                <SelectTrigger
                  data-testid="po-terms-select"
                  className="h-12 border-zinc-800 bg-zinc-900"
                >
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <SelectItem value="none">—</SelectItem>
                  {PAYMENT_TERMS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Order date
              </Label>
              <DateField
                testId="po-order-date"
                value={form.order_date}
                onChange={(v) => setForm({ ...form, order_date: v })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Expected date
              </Label>
              <DateField
                testId="po-expected-date"
                value={form.expected_date}
                onChange={(v) => setForm({ ...form, expected_date: v })}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Supplier reference
              </Label>
              <Input
                data-testid="po-reference-input"
                value={form.reference}
                onChange={(e) =>
                  setForm({ ...form, reference: e.target.value })
                }
                placeholder="Their quote or invoice number"
                className="h-12 border-zinc-800 bg-zinc-900"
              />
            </div>
            <label
              data-testid="po-consignment-toggle"
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:col-span-2"
            >
              <input
                type="checkbox"
                data-testid="po-consignment-input"
                checked={!!form.consignment}
                onChange={(e) =>
                  setForm({ ...form, consignment: e.target.checked })
                }
                className="h-5 w-5 accent-indigo-500"
              />
              <span>
                <span className="block text-sm font-semibold">
                  Consignment order
                </span>
                <span className="block text-xs text-zinc-500">
                  Slabs and sinks received from this PO are marked as
                  consignment stock
                </span>
              </span>
            </label>
          </div>

          <div
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="po-lines"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Items ordered
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="add-po-line"
                  variant="outline"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      lines: [...f.lines, { ...EMPTY_LINE, kind: "slab" }],
                    }))
                  }
                  className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                >
                  <Plus size={14} weight="bold" className="mr-2" /> Slab Line
                </Button>
                <Button
                  type="button"
                  data-testid="add-po-sink-line"
                  variant="outline"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      lines: [
                        ...f.lines,
                        {
                          ...EMPTY_LINE,
                          kind: "sink",
                          brand: "",
                          model: "",
                          sink_type: "Undermount",
                          size: "",
                        },
                      ],
                    }))
                  }
                  className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                >
                  <Plus size={14} weight="bold" className="mr-2" /> Sink Line
                </Button>
              </div>
            </div>
            <div
              className="mt-3 flex flex-wrap items-center gap-2"
              data-testid="po-restock-row"
            >
              <span className="text-[11px] uppercase tracking-widest text-zinc-500">
                Restock from inventory
              </span>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="po-restock-search"
                    className="h-11 w-full justify-start border-zinc-800 bg-zinc-950 text-sm font-normal text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 sm:w-96"
                  >
                    <MagnifyingGlass
                      size={15}
                      weight="bold"
                      className="mr-2 text-zinc-500"
                    />
                    Search inventory or add a new slab…
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[92vw] border-zinc-800 bg-zinc-900 p-0 sm:w-96"
                  data-testid="po-restock-popover"
                >
                  <Command className="bg-zinc-900">
                    <CommandInput
                      data-testid="po-restock-search-input"
                      placeholder="Search slabs & sinks…"
                      className="text-zinc-100"
                    />
                    <CommandList>
                      <CommandEmpty>No matching item in inventory.</CommandEmpty>
                      {lowStock.length > 0 && (
                        <CommandGroup heading="Running low — reorder">
                          {lowStock.map((row, i) => (
                            <CommandItem
                              key={`low-${row.slab_color}-${i}`}
                              value={`reorder low stock ${row.slab_color}`}
                              data-testid={`po-restock-low-${i}`}
                              onSelect={() => addLowStock(row)}
                              className="text-zinc-200"
                            >
                              <Warning
                                size={14}
                                weight="bold"
                                className="text-amber-400"
                              />
                              <span className="truncate">{row.slab_color}</span>
                              <span className="ml-auto shrink-0 text-[11px] text-amber-400/80">
                                {row.count} left
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      <CommandGroup heading="Add new">
                        <CommandItem
                          value="new slab line add blank"
                          data-testid="po-restock-new-slab"
                          onSelect={() => {
                            addLine("slab");
                            setPickerOpen(false);
                            toast.success("New slab line added");
                          }}
                          className="text-zinc-200"
                        >
                          <Plus
                            size={14}
                            weight="bold"
                            className="text-indigo-300"
                          />
                          New slab line
                        </CommandItem>
                        <CommandItem
                          value="new sink line add blank"
                          data-testid="po-restock-new-sink"
                          onSelect={() => {
                            addLine("sink");
                            setPickerOpen(false);
                            toast.success("New sink line added");
                          }}
                          className="text-zinc-200"
                        >
                          <Plus
                            size={14}
                            weight="bold"
                            className="text-indigo-300"
                          />
                          New sink line
                        </CommandItem>
                      </CommandGroup>
                      {catalog.slabs.length > 0 && (
                        <CommandGroup heading="Slabs in inventory">
                          {catalog.slabs.map((s, i) => (
                            <CommandItem
                              key={`slab-${s.key}`}
                              value={`slab ${s.slab_color} ${s.material} ${s.finish} ${s.thickness} ${s.key}`}
                              data-testid={`po-restock-slab-${i}`}
                              onSelect={() => {
                                pullFromInventory(`slab:${s.key}`);
                                setPickerOpen(false);
                              }}
                              className="text-zinc-200"
                            >
                              <span className="truncate">
                                {s.slab_color}
                                <span className="text-zinc-500">
                                  {" "}
                                  · {s.thickness} {s.finish}
                                </span>
                              </span>
                              <span className="ml-auto shrink-0 text-[11px] text-zinc-500">
                                {s.in_stock} in stock
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      {catalog.sinks.length > 0 && (
                        <CommandGroup heading="Sinks in inventory">
                          {catalog.sinks.map((s, i) => (
                            <CommandItem
                              key={`sink-${s.key}`}
                              value={`sink ${[s.brand, s.model].filter(Boolean).join(" ")} ${s.sink_type || ""} ${s.key}`}
                              data-testid={`po-restock-sink-${i}`}
                              onSelect={() => {
                                pullFromInventory(`sink:${s.key}`);
                                setPickerOpen(false);
                              }}
                              className="text-zinc-200"
                            >
                              <span className="truncate">
                                {[s.brand, s.model].filter(Boolean).join(" ") ||
                                  "Sink"}
                              </span>
                              <span className="ml-auto shrink-0 text-[11px] text-zinc-500">
                                {s.stocked === false
                                  ? "supplier stock"
                                  : `${s.in_stock} on hand`}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                variant="outline"
                data-testid="po-scan-barcode-button"
                onClick={() => setScanOpen(true)}
                className="h-11 border-zinc-800 bg-zinc-950 text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
              >
                <Barcode
                  size={15}
                  weight="bold"
                  className="mr-2 text-indigo-300"
                />
                Scan
              </Button>
            </div>
            <div className="mt-3 space-y-3">
              {form.lines.map((l, i) => (
                <div
                  key={i}
                  data-testid={`po-line-${i}`}
                  className="relative overflow-hidden rounded-md border border-zinc-800 bg-zinc-950 p-3 pl-4"
                >
                  <span
                    className={`absolute left-0 top-0 h-full w-1 ${
                      l.kind === "sink" ? "bg-indigo-500" : "bg-zinc-600"
                    }`}
                  />
                  <div className="mb-2 text-[10px] uppercase tracking-widest text-zinc-500">
                    {l.kind === "sink" ? "Sink line" : "Slab line"}
                  </div>
                  <div className="grid gap-2 lg:grid-cols-8">
                    {(l.kind === "sink"
                      ? [
                          ["brand", "Brand", "lg:col-span-2"],
                          ["model", "Model / SKU", "lg:col-span-2"],
                          ["sink_type", "Type", ""],
                          ["size", "Size", ""],
                          ["unit_cost", "Unit cost", ""],
                        ]
                      : [
                          ["slab_color", "Color", "lg:col-span-2"],
                          ["material", "Material", ""],
                          ["thickness", "Thickness", ""],
                          ["finish", "Finish", ""],
                          ["length_in", "Length in", ""],
                          ["width_in", "Width in", ""],
                          ["unit_cost", "Cost / ft²", ""],
                        ]
                    ).map(([k, ph, span]) => (
                      <Input
                        key={k}
                        data-testid={`po-line-${i}-${k}`}
                        value={l[k] ?? ""}
                        onChange={(e) => setLine(i, k, e.target.value)}
                        placeholder={ph}
                        className={`h-12 border-zinc-800 bg-zinc-900 ${span}`}
                      />
                    ))}
                    <div className="flex gap-2">
                      <Input
                        data-testid={`po-line-${i}-qty`}
                        type="number"
                        min="1"
                        value={l.qty}
                        onChange={(e) => setLine(i, "qty", e.target.value)}
                        className="h-12 border-zinc-800 bg-zinc-900"
                        placeholder="Qty"
                      />
                      <button
                        type="button"
                        data-testid={`remove-po-line-${i}`}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            lines: f.lines.filter((_, j) => j !== i),
                          }))
                        }
                        className="px-2 text-zinc-500 hover:text-red-400"
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                  {(() => {
                    const sq = lineSqft(l);
                    const unit = Number(l.unit_cost) || 0;
                    if (!sq && !unit && !hasOverride(l)) return null;
                    return (
                      <div
                        data-testid={`po-line-${i}-estimate`}
                        className="mt-2 flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-zinc-500"
                      >
                        <span className="font-mono-data">
                          {sq
                            ? `${fmtSqft(sq.sqft)}${sq.basis === "received" ? " (received)" : ""}${unit ? ` × ${fmtMoney(unit, form.currency)}/ft² = ` : ""}`
                            : `${Number(l.qty) || 0} × ${fmtMoney(unit, form.currency)} = `}
                          {(sq && !unit) ? "" : (
                            <span
                              className={`font-semibold ${hasOverride(l) ? "line-through text-zinc-500" : "text-zinc-200"}`}
                            >
                              {fmtMoney(computedLineTotal(l), form.currency)}
                            </span>
                          )}
                        </span>
                        <label className="flex items-center gap-1.5">
                          <span className="uppercase tracking-widest text-[9px] font-bold">
                            Adjust to
                          </span>
                          <Input
                            data-testid={`po-line-${i}-total_override`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={l.total_override ?? ""}
                            onChange={(e) => setLine(i, "total_override", e.target.value)}
                            placeholder="supplier total"
                            className="font-mono-data h-8 w-32 border-zinc-800 bg-zinc-900 text-right text-xs"
                          />
                        </label>
                        {hasOverride(l) && (
                          <span className="font-mono-data font-semibold text-amber-300">
                            = {fmtMoney(lineTotal(l), form.currency)}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>

          <div
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="po-charges"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Additional charges
              </div>
              <Button
                type="button"
                variant="outline"
                data-testid="add-po-charge"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    additional_charges: [
                      ...(f.additional_charges || []),
                      { label: "", amount: "" },
                    ],
                  }))
                }
                className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                <Plus size={14} weight="bold" className="mr-2" /> Add charge
              </Button>
            </div>
            <p className="mt-1 text-xs text-zinc-600">
              Freight, crating, tax or handling — added to the order total.
            </p>
            {(form.additional_charges || []).length === 0 ? (
              <p
                className="mt-3 text-sm text-zinc-500"
                data-testid="po-charges-empty"
              >
                No extra charges.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {form.additional_charges.map((c, i) => (
                  <div
                    key={i}
                    className="flex gap-2"
                    data-testid={`po-charge-${i}`}
                  >
                    <Input
                      data-testid={`po-charge-${i}-label`}
                      value={c.label}
                      placeholder="Freight, tax…"
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          additional_charges: f.additional_charges.map((x, j) =>
                            j === i ? { ...x, label: e.target.value } : x,
                          ),
                        }))
                      }
                      className="h-11 flex-1 border-zinc-800 bg-zinc-950"
                    />
                    <Input
                      data-testid={`po-charge-${i}-amount`}
                      type="number"
                      step="0.01"
                      value={c.amount}
                      placeholder="0.00"
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          additional_charges: f.additional_charges.map((x, j) =>
                            j === i ? { ...x, amount: e.target.value } : x,
                          ),
                        }))
                      }
                      className="h-11 w-32 border-zinc-800 bg-zinc-950"
                    />
                    <button
                      type="button"
                      data-testid={`remove-po-charge-${i}`}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          additional_charges: f.additional_charges.filter(
                            (_, j) => j !== i,
                          ),
                        }))
                      }
                      className="px-2 text-zinc-500 hover:text-red-400"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Notes
            </Label>
            <Textarea
              data-testid="po-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="border-zinc-800 bg-zinc-900"
            />
          </div>

          <div>
            <Button
              type="submit"
              data-testid="save-po-button"
              disabled={busy}
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400 sm:w-auto sm:px-8"
            >
              {busy ? "Saving…" : id ? "Save Changes" : "Create PO"}
            </Button>
          </div>
        </form>
        <BarcodeScanDialog
          open={scanOpen}
          onOpenChange={setScanOpen}
          onDetected={addByBarcode}
        />
      </main>
    </div>
  );
}
